import { createDraft, getDraft, normalizeDraftContext } from './cs-issue-domain.mjs'
import { resolveCSRole } from './cs-issue-permissions.mjs'
import { downloadCSIssueAttachment } from './cs-issue-export-download.mjs'
import { csIssueErrorMessage } from './cs-issue-api-adapter.mjs'
import { CS_ISSUE_API_UNAVAILABLE_MESSAGE, createCSIssueApiUnavailableError } from './cs-issue-api-availability.mjs'

const initial = apiEnabled => ({
  apiEnabled, apiUnavailableMessage: CS_ISSUE_API_UNAVAILABLE_MESSAGE,
  userId: null, role: 'user', tickets: [], total: 0, listMeta: {}, listLoading: false, listError: '',
  user: {}, sessionLoading: false, sessionError: '', isSuperAdmin: false,
  detail: null, detailLoading: false, detailError: '', pendingIds: [],
  csAssignees: [], technicalAssignees: [], staffError: '',
  notifications: [], unreadCount: 0, notificationLoading: false, notificationError: '',
  drafts: {}, nextDraftId: 1,
})

export function createCSIssueRepository(api, observable = value => value, options = {}) {
  const apiEnabled = options.enabled !== false
  const state = observable(initial(apiEnabled))
  let epoch = 0
  let listRequest = 0
  let detailRequest = 0
  let detailId = null
  const detailReads = new Map()
  let notificationRequest = 0
  let sessionRequest = 0
  let bootstrapPromise = null
  let lastFilters = { page: 1 }
  const context = () => ({ role: state.role, userId: state.userId, staff: [...state.csAssignees, ...state.technicalAssignees] })
  const sameId = (a, b) => a != null && b != null && String(a) === String(b)
  const cached = id => sameId(state.detail?.id, id) ? state.detail : state.tickets.find(item => sameId(item.id, id))
  const replaceTicket = ticket => {
    if (sameId(detailId, ticket.id)) state.detail = ticket
    state.tickets = state.tickets.map(item => sameId(item.id, ticket.id) ? ticket : item)
  }
  async function readTicket(id) {
    const key = String(id)
    const request = (detailReads.get(key) || 0) + 1
    detailReads.set(key, request)
    const session = epoch
    try {
      const ticket = await api.getIssue(id, context())
      return session === epoch && detailReads.get(key) === request ? ticket : null
    } catch (error) {
      if (session !== epoch || detailReads.get(key) !== request) return null
      throw error
    }
  }
  function invalidateTicket(id, error) {
    detailReads.set(String(id), (detailReads.get(String(id)) || 0) + 1)
    state.tickets = state.tickets.filter(item => !sameId(item.id, id))
    if (sameId(detailId, id)) { state.detail = null; state.detailError = csIssueErrorMessage(error); state.detailLoading = false }
  }
  const ensureAvailable = () => {
    if (!state.apiEnabled) throw createCSIssueApiUnavailableError()
  }
  async function mutate(id, operation) {
    ensureAvailable()
    if (state.pendingIds.some(value => sameId(value, id))) throw new Error('변경 사항을 저장하고 있습니다.')
    const current = cached(id)
    if (current?.version == null) throw new Error('최신 문의 정보를 먼저 불러와 주세요.')
    const session = epoch
    state.pendingIds = [...state.pendingIds, id]
    try {
      try { await operation(current) }
      catch (error) {
        if (session === epoch && [403, 404, 409].includes(error?.response?.status)) {
          listRequest += 1
          state.listLoading = false
          if (error.response.status === 409) {
            try { const latest = await readTicket(id); if (latest) replaceTicket(latest) }
            catch (refreshError) { if (session === epoch) invalidateTicket(id, refreshError) }
          } else invalidateTicket(id, error)
        }
        throw error
      }
      if (session !== epoch) throw new Error('로그인 정보가 변경되었습니다. 목록을 다시 확인해 주세요.')
      listRequest += 1
      state.listLoading = false
      try {
        const ticket = await readTicket(id)
        if (ticket) replaceTicket(ticket)
      } catch (error) {
        if (session === epoch) {
          const message = '변경 사항은 반영되었지만 최신 정보를 불러오지 못했습니다. 새로고침해 주세요.'
          if (sameId(detailId, id)) { state.detail = null; state.detailError = message; state.detailLoading = false }
          state.tickets = state.tickets.filter(item => !sameId(item.id, id))
          state.listError = message
        }
      }
    } finally {
      if (session === epoch) state.pendingIds = state.pendingIds.filter(value => !sameId(value, id))
    }
  }
  const repository = {
    state,
    initializeRole(permission) { state.role = resolveCSRole({ organization_permission: permission }); return state.role },
    initializeSession(user = {}) {
      const id = user.pk ?? user.id ?? user.user_id ?? null
      if (state.userId != null && (!sameId(state.userId, id) || state.user.organization_permission !== user.organization_permission)) this.resetForUser(user.organization_permission)
      const changed = !sameId(state.userId, id) || state.user.organization_permission !== user.organization_permission
      state.userId = id
      state.user = { ...user }
      state.isSuperAdmin = user.organization_permission === 'super_admin'
      if (changed) this.initializeRole(user.organization_permission)
      return state.role
    },
    async bootstrapSession(user = {}, { force = false } = {}) {
      this.initializeSession(user)
      if (bootstrapPromise && !force) return bootstrapPromise
      const request = ++sessionRequest
      const session = epoch
      state.sessionLoading = true
      state.sessionError = ''
      const run = async () => {
        try {
          const roles = []
          if (state.isSuperAdmin) {
            let page = 1
            let maxPage = 1
            do {
              const result = await api.staffRoles({ search: user.email || '', page })
              if (session !== epoch || request !== sessionRequest) return
              roles.push(...result.items)
              maxPage = result.maxPage || 1
              page += 1
            } while (page <= maxPage)
          }
          if (session === epoch && request === sessionRequest) state.role = resolveCSRole(user, roles)
        } catch (error) {
          if (session === epoch && request === sessionRequest) {
            state.role = resolveCSRole(user)
            state.sessionError = csIssueErrorMessage(error)
            state.detail = null
            state.tickets = []
          }
        } finally {
          if (session === epoch && request === sessionRequest) { state.sessionLoading = false; bootstrapPromise = null }
        }
        return state.role
      }
      bootstrapPromise = run()
      const result = await bootstrapPromise
      if (request === sessionRequest) bootstrapPromise = null
      return result
    },
    resetForUser(permission) {
      epoch += 1
      sessionRequest += 1
      bootstrapPromise = null
      detailId = null
      detailReads.clear()
      Object.assign(state, initial(apiEnabled), { role: resolveCSRole({ organization_permission: permission }) })
    },
    getTicket(id) { return sameId(state.detail?.id, id) ? state.detail : null },
    async loadTickets(filters = {}) {
      if (!state.apiEnabled) {
        state.tickets = []
        state.total = 0
        state.listMeta = {}
        state.listLoading = false
        state.listError = ''
        return
      }
      const request = ++listRequest
      lastFilters = { ...filters }
      const session = epoch
      state.listLoading = true
      state.listError = ''
      try {
        const result = await api.listIssues(filters, context())
        if (request !== listRequest || session !== epoch) return
        state.tickets = result.items
        state.total = result.total
        state.listMeta = { ...result.meta, page: result.page, limit: result.limit, maxPage: result.maxPage }
      } catch (error) {
        if (request === listRequest && session === epoch) { state.tickets = []; state.total = 0; state.listError = csIssueErrorMessage(error) }
      } finally {
        if (request === listRequest && session === epoch) state.listLoading = false
      }
    },
    async loadTicket(id) {
      detailId = id
      if (!state.apiEnabled) {
        state.detail = null
        state.detailLoading = false
        state.detailError = CS_ISSUE_API_UNAVAILABLE_MESSAGE
        return
      }
      const request = ++detailRequest
      const session = epoch
      state.detail = null
      state.detailLoading = true
      state.detailError = ''
      try {
        const ticket = await readTicket(id)
        if (ticket && request === detailRequest && session === epoch) state.detail = ticket
      } catch (error) {
        if (request === detailRequest && session === epoch) state.detailError = csIssueErrorMessage(error)
      } finally {
        if (request === detailRequest && session === epoch) state.detailLoading = false
      }
    },
    async loadStaff() {
      if (!state.apiEnabled) {
        state.csAssignees = []
        state.technicalAssignees = []
        state.staffError = ''
        return
      }
      if (state.role !== 'cs_admin') { state.csAssignees = []; state.technicalAssignees = []; return }
      const session = epoch
      state.staffError = ''
      try {
        const [cs, technical] = await Promise.all([api.staff('cs'), api.staff('technical')])
        if (session === epoch) { state.csAssignees = cs; state.technicalAssignees = technical }
      } catch (error) { if (session === epoch) { state.csAssignees = []; state.technicalAssignees = []; state.staffError = csIssueErrorMessage(error) } }
    },
    async createTicket(input, requestId) {
      ensureAvailable()
      const session = epoch
      const ticket = await api.createIssue(input, requestId)
      if (session !== epoch) throw new Error('로그인 정보가 변경되었습니다. 목록을 다시 확인해 주세요.')
      return ticket
    },
    updateTicket(id, patch) { return mutate(id, ticket => api.updateIssue(id, patch, ticket.version)) },
    statusAction(id, action, reason, requestId) { return mutate(id, ticket => api.statusAction(id, action, reason, ticket.version, requestId)) },
    correctTicketStatus(id, status, reason, requestId) { return mutate(id, ticket => api.correctStatus(id, status, reason, ticket.version, requestId)) },
    setCSAssignees(id, ids) { return mutate(id, ticket => api.assign(id, 'cs', [...new Set(ids.map(Number))], ticket.version)) },
    setTechnicalAssignees(id, ids) { return mutate(id, ticket => api.assign(id, 'technical', [...new Set(ids.map(Number))], ticket.version)) },
    addComment(id, input, requestId) { return mutate(id, () => api.addComment(id, input, requestId)) },
    addInternalNote(id, body, requestId) { return mutate(id, () => api.addComment(id, { body, visibility: 'internal' }, requestId)) },
    editComment(id, commentId, body) {
      return mutate(id, ticket => {
        const comment = [...ticket.comments, ...(ticket.internalNotes || [])].find(item => sameId(item.id, commentId))
        if (comment?.version == null) throw new Error('최신 댓글 정보를 먼저 불러와 주세요.')
        return api.editComment(id, commentId, body, comment.version)
      })
    },
    deleteComment(id, commentId, reason) {
      return mutate(id, ticket => {
        const comment = [...ticket.comments, ...(ticket.internalNotes || [])].find(item => sameId(item.id, commentId))
        if (comment?.version == null) throw new Error('최신 댓글 정보를 먼저 불러와 주세요.')
        return api.deleteComment(id, commentId, reason, comment.version)
      })
    },
    deleteAttachment(id, attachmentId, reason) { return mutate(id, () => api.deleteAttachment(id, attachmentId, reason)) },
    downloadAttachment(id, attachmentId, options) { ensureAvailable(); return downloadCSIssueAttachment(api, id, attachmentId, options) },
    staffRoles(options) { return api.staffRoles(options) },
    staffUsers() { return api.staffUsers() },
    async saveStaffRole(input, id) {
      const session = epoch
      if (id == null) await api.createStaffRole(input)
      else await api.updateStaffRole(id, input)
      if (session !== epoch) throw new Error('로그인 정보가 변경되었습니다.')
      state.detail = null
      state.tickets = []
      await this.bootstrapSession(state.user, { force: true })
      await this.loadStaff()
    },
    async markAsRead(id) {
      if (!state.apiEnabled) return
      const session = epoch
      await api.markIssueRead(id)
      if (session !== epoch) return
      const ticket = await readTicket(id)
      if (session !== epoch) return
      if (ticket) replaceTicket(ticket)
      await Promise.all([this.loadTickets(lastFilters), this.loadUnreadCount()])
    },
    getNotifications() { return state.notifications },
    async loadUnreadCount() {
      const session = epoch
      const count = await api.unreadCount()
      if (session === epoch) state.unreadCount = count
      return count
    },
    async loadNotifications(options = {}) {
      if (!state.apiEnabled) {
        state.notifications = []
        state.notificationLoading = false
        state.notificationError = ''
        return { items: [], total: 0, page: 1, limit: options.pageSize || options.limit || 30 }
      }
      const session = epoch
      const request = ++notificationRequest
      state.notificationLoading = true
      state.notificationError = ''
      try {
        const result = await api.notifications(options)
        if (session === epoch && request === notificationRequest) state.notifications = result.items
        return result
      } catch (error) {
        if (session === epoch && request === notificationRequest) { state.notifications = []; state.notificationError = csIssueErrorMessage(error) }
        throw error
      } finally { if (session === epoch && request === notificationRequest) state.notificationLoading = false }
    },
    async notificationSummary() {
      if (!state.apiEnabled) return { items: [], unreadCount: 0 }
      const [result, unreadCount] = await Promise.all([api.notifications({ page: 1 }), this.loadUnreadCount()])
      return { items: result.items, unreadCount }
    },
    async markNotificationRead(id) {
      if (!state.apiEnabled) return
      const session = epoch
      await api.markNotificationRead(id)
      if (session === epoch) {
        state.notifications = state.notifications.map(item => sameId(item.id, id) ? { ...item, isRead: true } : item)
        await this.loadUnreadCount()
      }
    },
    async markAllNotificationsRead() {
      if (!state.apiEnabled) return 0
      const session = epoch
      const result = await api.markAllNotificationsRead()
      if (session === epoch) { state.notifications = state.notifications.map(item => ({ ...item, isRead: true })); await this.loadUnreadCount() }
      return result
    },
    createDraft(source, payload) { return createDraft(state, normalizeDraftContext(source, payload)) },
    getDraft(id) { return getDraft(state, id) },
  }
  return repository
}
